/**
 * @file BkDialog base element. Import map name: "@theme/dialog". Registered as <bk-dialog>.
 *
 * Markup:
 *   <bk-dialog id="SearchDialog">
 *     <dialog aria-labelledby="SearchDialogTitle"> ... <button type="button" data-dialog-close>...</button></dialog>
 *   </bk-dialog>
 *   <button type="button" data-dialog-open="SearchDialog" aria-expanded="false">...</button>
 *
 * Behaviour:
 * - open(trigger): showModal(), focus trap, scroll lock (class `bk-scroll-lock` on <html> plus
 *   `--bk-scrollbar-width`), `open` attribute on the host, aria-expanded="true" on a trigger that
 *   has aria-expanded, publishes `bk:dialog:open` { id }.
 * - close(): closes, returns focus to the trigger, publishes `bk:dialog:close` { id }.
 * - Esc closes (native cancel). A click on the backdrop closes unless the host has
 *   `data-no-light-dismiss`. Any [data-dialog-close] inside closes. Any [data-dialog-open="<id>"]
 *   in the document opens (plain clicks only, so a /cart link still works with modifier keys).
 * - Theme editor: opens when a block inside is selected, or when its section is selected and the
 *   host has `data-editor-open`; closes again on deselect.
 * - Subclass for components (class BkCartDrawer extends BkDialog) and call super in overrides.
 */
import { FocusTrap, publish } from '@theme/utils';

const SCROLL_LOCK_CLASS = 'bk-scroll-lock';

/** @type {Set<HTMLElement>} */
const openDialogs = new Set();

/**
 * @param {HTMLElement} host
 * @param {number} scrollbarWidth - Measured by the caller before the dialog opened (a read then is
 *   free; after showModal() it forces a layout of the whole page)
 */
function lockScroll(host, scrollbarWidth) {
  if (openDialogs.size === 0) {
    const root = document.documentElement;
    // A custom property on <html> restyles every element of the page. Phones have no scrollbar
    // (0 px), so it is written only where a scrollbar really exists (measured: the search panel
    // took 536 ms to open on a Lighthouse phone, 155 ms of it forced layout from this function).
    if (scrollbarWidth > 0) root.style.setProperty('--bk-scrollbar-width', `${scrollbarWidth}px`);
    // Locking the page's scroll relayouts the whole page: done once the panel is on screen (two
    // frames), so the tap itself only pays for the panel. Skipped if the panel closed meanwhile.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (openDialogs.size > 0) root.classList.add(SCROLL_LOCK_CLASS);
      })
    );
  }
  openDialogs.add(host);
}

/** @param {HTMLElement} host */
function unlockScroll(host) {
  openDialogs.delete(host);
  if (openDialogs.size > 0) return;
  const root = document.documentElement;
  root.classList.remove(SCROLL_LOCK_CLASS);
  if (root.style.getPropertyValue('--bk-scrollbar-width')) root.style.removeProperty('--bk-scrollbar-width');
}

/*
 * The phone's Back button closes the open panel instead of leaving the page. Recent Chrome does it
 * for a modal <dialog> on its own, but the Facebook and Instagram in-app browsers (most of an ad
 * shop's visitors), older Android browsers and iOS swipe-back take Back as "leave the page", and
 * a shopper with the cart open was thrown off the site. So opening the first panel adds one
 * history entry (same URL); Back pops it and closes the top panel; closing a panel any other way
 * (X, backdrop, Esc, a button) removes the entry again, so no Back press is ever wasted. A link or
 * form that leaves the page never triggers a history.back(), which could cancel the navigation.
 */
const HISTORY_KEY = 'bkDialog';
let historyEntry = false;
let closingFromHistory = false;
let leaving = false;
/** @type {number} */
let backTimer = 0;
const historyEnabled = !window.Shopify?.designMode && typeof history.pushState === 'function';

if (historyEnabled) {
  const markLeaving = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (event.type === 'submit') {
      if (!(target instanceof HTMLFormElement) || target.method === 'dialog') return;
      leaving = true;
      return;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = target?.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    const url = new URL(link.href, location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search && url.hash) return;
    leaving = true;
  };
  // Checked once the event has finished its round, so a click another script cancels is not a leave.
  /** @type {number} */
  let leavingTimer = 0;
  const check = (event) =>
    window.setTimeout(() => {
      if (event.defaultPrevented) return;
      markLeaving(event);
      // A navigation that never happened (a download, an app link) must not freeze the entry.
      clearTimeout(leavingTimer);
      if (leaving) leavingTimer = window.setTimeout(() => { leaving = false; }, 10000);
    }, 0);
  document.addEventListener('click', check);
  document.addEventListener('submit', check);
  window.addEventListener('pageshow', () => { leaving = false; });
  window.addEventListener('popstate', () => {
    if (!historyEntry || history.state?.[HISTORY_KEY]) return;
    // Back was pressed while a panel was open: the entry is gone, close the panel on top.
    historyEntry = false;
    const top = [...openDialogs].pop();
    if (!(top instanceof BkDialog)) return;
    closingFromHistory = true;
    try {
      top.close();
    } finally {
      closingFromHistory = false;
    }
    // A panel under it (quick view under the cart) gets its own entry back.
    if (openDialogs.size > 0) pushEntry();
  });
}

function pushEntry() {
  if (!historyEnabled || historyEntry) return;
  clearTimeout(backTimer);
  history.pushState({ ...(history.state ?? {}), [HISTORY_KEY]: true }, '');
  historyEntry = true;
}

function releaseEntry() {
  if (!historyEnabled || !historyEntry || closingFromHistory) return;
  // Wait a moment: one panel often closes as another opens (quick view, then the cart). The
  // entry is kept for the new one instead of being popped and pushed again.
  clearTimeout(backTimer);
  backTimer = window.setTimeout(() => {
    if (openDialogs.size > 0 || !historyEntry) return;
    historyEntry = false;
    if (leaving || !history.state?.[HISTORY_KEY]) return;
    history.back();
  }, 60);
}

export class BkDialog extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {FocusTrap | null} */
  #trap = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  #pointerDownOutside = false;

  #openedByEditor = false;

  connectedCallback() {
    const dialog = this.querySelector(':scope > dialog') ?? this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#dialog = dialog;
    this.#trap = new FocusTrap(dialog);
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    dialog.addEventListener('cancel', (event) => this.#handleCancel(event), { signal });
    dialog.addEventListener('close', () => this.#teardown(), { signal });
    dialog.addEventListener('pointerdown', (event) => this.#handlePointerDown(event), { signal });
    dialog.addEventListener('click', (event) => this.#handleBackdropClick(event), { signal });
    this.addEventListener('click', (event) => this.#handleCloseClick(event), { signal });
    document.addEventListener('click', (event) => this.#handleOpenerClick(event), { signal });
    document.addEventListener('shopify:section:select', (event) => this.#handleEditorSelect(event, 'section'), { signal });
    document.addEventListener('shopify:block:select', (event) => this.#handleEditorSelect(event, 'block'), { signal });
    document.addEventListener('shopify:section:deselect', () => this.#handleEditorDeselect(), { signal });
    document.addEventListener('shopify:block:deselect', () => this.#handleEditorDeselect(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    if (openDialogs.has(this)) {
      this.#trap?.deactivate({ restoreFocus: false });
      unlockScroll(this);
    }
  }

  /** @returns {boolean} */
  get isOpen() {
    return Boolean(this.#dialog?.open);
  }

  /**
   * Open as a modal dialog.
   * @param {HTMLElement | null} [trigger] - Control that opened it; receives focus on close
   */
  open(trigger) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open || !dialog.isConnected) return;

    const focused = document.activeElement;
    this.#trigger = trigger ?? (focused instanceof HTMLElement ? focused : null);
    // Read before anything changes: the layout is still clean, so this costs nothing.
    const scrollbarWidth = openDialogs.size === 0 ? Math.max(0, window.innerWidth - document.documentElement.clientWidth) : 0;

    dialog.showModal();
    const landing = dialog.querySelector('[data-dialog-focus]');
    if (landing instanceof HTMLElement) landing.focus({ preventScroll: true });
    lockScroll(this, scrollbarWidth);
    pushEntry();
    this.#trap?.activate(this.#trigger);
    this.setAttribute('open', '');
    this.#setTriggerExpanded(true);
    publish('bk:dialog:open', { id: this.id });
  }

  /** Close the dialog and return focus to the trigger. */
  close() {
    const dialog = this.#dialog;
    if (!dialog) return;
    if (dialog.open) dialog.close();
    this.#teardown();
  }

  /** Runs once per opening, whether closed by close(), Esc, a form[method="dialog"] or the browser. */
  #teardown() {
    if (!openDialogs.has(this)) return;
    unlockScroll(this);
    releaseEntry();
    this.removeAttribute('open');
    this.#setTriggerExpanded(false);
    this.#trap?.deactivate();
    this.#trigger = null;
    this.#openedByEditor = false;
    publish('bk:dialog:close', { id: this.id });
  }

  /** @param {boolean} expanded */
  #setTriggerExpanded(expanded) {
    const trigger = this.#trigger;
    if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', String(expanded));
  }

  /** @param {Event} event */
  #handleCancel(event) {
    event.preventDefault();
    this.close();
  }

  /**
   * @param {MouseEvent} event
   * @returns {boolean} True when the pointer is on the backdrop (outside the dialog box)
   */
  #isOnBackdrop(event) {
    const dialog = this.#dialog;
    if (!dialog || event.target !== dialog) return false;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    return !inside;
  }

  /** @param {PointerEvent} event */
  #handlePointerDown(event) {
    this.#pointerDownOutside = this.#isOnBackdrop(event);
  }

  /** @param {MouseEvent} event */
  #handleBackdropClick(event) {
    const startedOutside = this.#pointerDownOutside;
    this.#pointerDownOutside = false;
    if (this.hasAttribute('data-no-light-dismiss') || event.detail === 0) return;
    if (startedOutside && this.#isOnBackdrop(event)) this.close();
  }

  /** @param {MouseEvent} event */
  #handleCloseClick(event) {
    const target = event.target;
    const closer = target instanceof Element ? target.closest('[data-dialog-close]') : null;
    if (!closer || closer.closest('dialog') !== this.#dialog) return;
    event.preventDefault();
    this.close();
  }

  /** @param {MouseEvent} event */
  #handleOpenerClick(event) {
    if (!this.id || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    const opener = target instanceof Element ? target.closest('[data-dialog-open]') : null;
    if (!(opener instanceof HTMLElement) || opener.dataset.dialogOpen !== this.id) return;
    event.preventDefault();
    this.open(opener);
  }

  /**
   * @param {Event} event
   * @param {'section' | 'block'} kind
   */
  #handleEditorSelect(event, kind) {
    const target = event.target;
    if (!(target instanceof Node) || this.isOpen) return;
    const wanted = kind === 'block' ? this.contains(target) : this.hasAttribute('data-editor-open') && target.contains(this);
    if (!wanted) return;
    this.open();
    this.#openedByEditor = true;
  }

  #handleEditorDeselect() {
    if (this.#openedByEditor) this.close();
  }
}

if (!customElements.get('bk-dialog')) customElements.define('bk-dialog', BkDialog);
